<script lang="ts">
	import { _ } from 'svelte-i18n';
	import type { Post } from '$lib/api/rpc';
	import { onMount, afterUpdate } from 'svelte';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import Loading from '$lib/components/Post/PostBlock/Blocks/Loading.svelte';
	import Image from '$lib/components/Post/PostBlock/Blocks/Image.svelte';
	import { page } from '$app/stores';
	import { NodeViewWrapper } from 'svelte-tiptap';
	import DragAndDropWrapper from '$lib/components/TextEditor/Components/DragAndDropWrapper/DragAndDropWrapper.svelte';
	import type { Editor, NodeViewProps } from '@tiptap/core';
	import Icon from '$lib/components/Icon/Icon.svelte';
	import { fileUploadStore } from '$lib/components/FileUploader/fileUploadStore';

	import Popover from '$lib/components/Popover/Popover.svelte';
	import { addPreviewImages, removePreviewImages } from '$textEditor/PostComposer/composerStore';

	export let node: NodeViewProps['node'];
	export let deleteNode: () => void;
	export let updateAttributes: NodeViewProps['updateAttributes'];
	export let editor: Editor;

	if (!$page.data.respHub || !$page.data.restApi)
		throw new Error('TextEditor Image: Missing hub or restApi');

	const hubId = $page.data.respHub.hub.id;
	const restApi = $page.data.restApi;

	let uploading = false;
	let post: Post | null;

	const deleteNodeWrapper = () => {
		if (post?.title_image?.url) {
			removePreviewImages([post.title_image.url]);
		}
		editor.setEditable(true);
		deleteNode();
	};

	const handleDocumentUpload = async () => {
		uploading = true;
		const file = $fileUploadStore[$fileUploadStore.length - 1];
		$fileUploadStore = [];
		if (!file) return;
		editor.setEditable(false);
		const response = await restApi.uploadFile(hubId, file);
		if (response.error) {
			toastMessages.danger('', response.error);
			editor.setEditable(true);
			deleteNodeWrapper();
		} else if (response.data) {
			post = response.data.asset;
			updateAttributes({ post });
			if (post.title_image?.url) addPreviewImages([post.title_image.url]);
		}
		editor.setEditable(true);
		uploading = false;
	};

	// TODO: For some reason, this component is mounted twice,
	// currently we are hacking around this by exiting early in handleDocumentUpload and setPreviewImage
	onMount(() => {
		post = node.attrs.post;
		alignState = post?.etc.align as Align;
		minimal = node.attrs.minimal;
		url = post?.etc.link_url || '';
		altText = post?.title_image?.alt_text || '';

		if (!post) {
			void handleDocumentUpload();
			return;
		}
		if (post.title_image?.url) addPreviewImages([post.title_image.url]);
	});

	let minimal = false;

	type Align = undefined /* meaning center aligned */ | 'left' | 'right';
	type AlignmentIconType = 'full-width' | 'align-left' | 'align-right';

	let alignArray: Array<Align> = [undefined, 'left', 'right'];

	let alignState: Align = undefined;

	const convertAlignStateToIcon = (align: Align) => {
		let icon: AlignmentIconType;
		switch (align) {
			case 'left':
				icon = 'align-left';
				break;
			case 'right':
				icon = 'align-right';
				break;
			default:
				icon = 'full-width';
		}

		return icon;
	};

	let wrapperEl: HTMLElement;

	const setFloat = () => {
		// This is a wrapper div that is created by svelte-tiptap, we should maybe raise an upstream issue to be able to inject custom classes
		const closestImageComponent = wrapperEl.closest('.node-hubsImageComponent') as HTMLElement;
		if (closestImageComponent) {
			closestImageComponent.style.clear = 'both';
			switch (alignState) {
				case 'left':
					closestImageComponent.style.inlineSize = '51%';
					closestImageComponent.style.float = 'left';
					break;
				case 'right':
					closestImageComponent.style.inlineSize = '51%';
					closestImageComponent.style.float = 'right';
					break;
				default:
					closestImageComponent.style.inlineSize = '100%';
					closestImageComponent.style.float = 'none';
			}
		}
	};

	afterUpdate(() => {
		setFloat();
	});

	const updateAlignState = (newState: Align) => {
		alignState = newState;
		setFloat();
		updateAttributes({ post: { ...post, etc: { ...post?.etc, align: alignState } } });
	};

	const updateDescription = (newDescription: string) => {
		updateAttributes({ post: { ...post, description: newDescription } });
	};

	let url = '';
	let altText = '';
	let isPopoverUrlOpen = false;
	let isPopoverAltOpen = false;

	const handleInputKeydown = (event: KeyboardEvent, type: 'url' | 'alt') => {
		const newValue = (event.target as HTMLInputElement).value;
		switch (type) {
			case 'url':
				url = newValue;
				updateAttributes({
					post: { ...post, etc: { ...post?.etc, link_url: newValue } },
				});
				break;
			case 'alt':
				altText = newValue;
				updateAttributes({
					post: { ...post, title_image: { ...post?.title_image, alt_text: newValue } },
				});
				break;
		}

		if (event.key === 'Enter') {
			event.preventDefault();
			isPopoverUrlOpen = false;
			isPopoverAltOpen = false;
		}
	};
</script>

<NodeViewWrapper>
	<div bind:this={wrapperEl}>
		<DragAndDropWrapper on:delete={deleteNodeWrapper}>
			<div slot="header" class="header">
				{#if !minimal}
					{#each alignArray as align}
						<button type="button" class="inlineIcon" on:click={() => updateAlignState(align)}>
							<Icon
								color={alignState == align ? '#0c8ef0' : '#95a6b4'}
								icon={convertAlignStateToIcon(align)}
								clickable
							/>
						</button>
					{/each}
					<Popover isOpen={isPopoverUrlOpen} color="#2a3039">
						<button type="button" slot="target">
							<Icon
								color={url ? '#0c8ef0' : '#95a6b4'}
								icon="link"
								clickable
								onClick={() => {
									document.getElementById('urlInput')?.focus();
									isPopoverUrlOpen = true;
								}}
							/>
						</button>
						<div slot="content" class="urlInput">
							<input
								id="urlInput"
								placeholder={$_('composer.placeholder.paste.or.type.link')}
								type="url"
								value={url || ''}
								on:keyup={(event) => {
									handleInputKeydown(event, 'url');
								}}
							/>
						</div>
					</Popover>
					<Popover isOpen={isPopoverAltOpen} color="#2a3039">
						<button type="button" slot="target">
							<Icon
								color={altText ? '#0c8ef0' : '#95a6b4'}
								icon="edit"
								clickable
								onClick={() => {
									document.getElementById('editInput')?.focus();
									isPopoverAltOpen = true;
								}}
							/>
						</button>
						<div slot="content" class="editInput">
							<input
								id="editInput"
								placeholder={$_('composer.placeholder.add.alt.text')}
								type="text"
								value={altText || ''}
								on:keyup={(event) => {
									handleInputKeydown(event, 'alt');
								}}
							/>
						</div>
					</Popover>
				{/if}
			</div>
			{#if uploading}
				<Loading />
			{:else if post}
				<div class="wrapper">
					<Image inComposer={true} block={post} />
					{#if !minimal}
						<input
							placeholder={$_('composer.placeholder.add.caption')}
							value={post.description}
							on:keyup={(e) => {
								if (e.target) {
									//@ts-ignore
									updateDescription(e.target.value);
								}
							}}
						/>
					{/if}
				</div>
			{/if}
		</DragAndDropWrapper>
	</div>
</NodeViewWrapper>

<style>
	.urlInput,
	.editInput {
		display: flex;
		flex-direction: row;
		align-items: center;
		border: none;
		border-radius: 5px;
		background-color: #2a3039 !important;
	}

	.urlInput input,
	.editInput input {
		min-inline-size: 25rem;
		padding: var(--gutterSmall);
		border: none;
		outline: none;
		background-color: #2a3039 !important;
		color: white;
	}

	button {
		border: none;
		background: none;
		cursor: pointer;
	}

	/* When an image is aligned to left/right, the size of the wrapper should allow 6 lines of text beside it (to match bluesuede)  */
	.wrapper {
		position: relative;
		overflow: auto;
	}

	.wrapper input {
		position: absolute;
		inset-block-end: 0;
		inline-size: 100%;
		border: none;
		background: white;
		text-align: center;
	}

	.header {
		display: flex;
		justify-content: space-between;
	}
</style>
